import React, { useState, useEffect } from "react";
import api from "../lib/apiClient";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

// Admin-only generic record editor.
// fields: [{ name, label, type?: 'text'|'number', full?: bool }]
export default function AdminEditDialog({ title, endpoint, fields, initial, onSaved, testid = "admin-edit" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const f = {};
    fields.forEach((fl) => { f[fl.name] = initial?.[fl.name] ?? ""; });
    setForm(f);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      fields.forEach((fl) => {
        const v = form[fl.name];
        if (v === "" || v == null) return;
        payload[fl.name] = fl.type === "number" ? Number(v) : v;
      });
      await api.put(endpoint, payload);
      toast.success("Changes saved");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" data-testid={`${testid}-btn`} onClick={() => setOpen(true)}>
        <PencilSimple size={16} className="mr-1" /> Edit
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {fields.map((fl) => (
              <div key={fl.name} className={fl.full ? "sm:col-span-2" : ""}>
                <Label className="text-xs text-zinc-400">{fl.label}</Label>
                <Input data-testid={`${testid}-${fl.name}`} type={fl.type === "number" ? "number" : "text"}
                  value={form[fl.name] ?? ""} onChange={(e) => setForm({ ...form, [fl.name]: e.target.value })}
                  className="mt-1 bg-zinc-950" />
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-500">Only changed fields are updated. Blank fields are left unchanged.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid={`${testid}-save`} onClick={save} disabled={saving} className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
